import { useCallback, useEffect, useRef, type MutableRefObject } from 'react';
import type L from 'leaflet';
import * as turf from '@turf/turf';

import { wgs84ToLv95ForOneCoordinate } from '../map/coordinateTransforms';

export const POLYGON_DRAW_HINT =
  'Click to place a point, hold Shift to keep the new side at 90° from the previous one, double click to end polygon; click & drag to pan.';
export const RECTANGLE_DRAW_HINT = 'Click and drag to draw rectangle.';
export const EDIT_DRAW_HINT =
  'Click on large points to drag, click on small points to create new ones, hold the rotate icon to rotate manually or click it to rotate by a precise angle.';
export const BUILDABLE_OFFSET_HINT = 'Select side of current area and enter inward offset to update buildable area.';
export const PARCEL_SELECTION_HINT = 'Select one or more adjacent parcels and Save.';

export type Lv95Coordinate = { x: number; y: number };
export type Wgs84Coordinate = { lng: number; lat: number };
export type BorderType = 'legalBorder' | 'buildableBorder' | 'analysisArea';
export type ShapeType = 'polygon' | 'rectangle';
export type PolygonRef = MutableRefObject<L.Polygon | null>;
export type TurfPolygon = ReturnType<typeof turf.polygon>;
export type SetGridParams = (params: Record<string, unknown>) => void;

export interface EditLayerHandlers {
  onEdit: (e: { layer: L.Layer }) => void;
  onVertexRemoved: (e: { layer: L.Layer }) => void;
}

export interface AnalysisAreaClickHandler {
  layer: L.Polygon;
  handler: (event: L.LeafletMouseEvent) => void;
}

export interface PointerCoordinates {
  clientX: number;
  clientY: number;
}

export interface ManualRotateSession {
  borderType: BorderType;
  layerRef: L.Polygon;
  map: L.Map;
  pivotLatLng: L.LatLng;
  baseLatLngs: L.LatLng[];
  startPointerAngle: number;
  lastAngleDegrees: number;
}

type LayerWithMap = { _map?: L.Map };

export const getLayerMap = (layer: L.Layer | null | undefined): L.Map | undefined =>
  (layer as LayerWithMap | null | undefined)?._map ?? undefined;

export const getLayerOuterRing = (layer: L.Layer | null | undefined): L.LatLng[] => {
  const latLngs = (layer as L.Polyline | null | undefined)?._latlngs;
  return Array.isArray(latLngs?.[0]) ? latLngs[0] : [];
};

export const layerRingToLv95 = (layer: L.Layer | null | undefined): Lv95Coordinate[] =>
  getLayerOuterRing(layer).map((coord) => wgs84ToLv95ForOneCoordinate(coord.lng, coord.lat));

export const useStableHandler = <TArgs extends unknown[]>(handler: (...args: TArgs) => void) => {
  const handlerRef = useRef(handler);
  useEffect(() => {
    handlerRef.current = handler;
  }, [handler]);
  return useCallback((...args: TArgs) => handlerRef.current?.(...args), []);
};

export const useParcelSelectionOutsideClick = (enabled: boolean, onOutsideClick: () => void) => {
  useEffect(() => {
    if (!enabled) return;

    const handleDocumentClick = (event: MouseEvent) => {
      const target = event.target;
      if (!(target instanceof Element) || target.closest('.grid-map-shell')) return;
      if (
        target.closest('[data-border-action]') ||
        target.closest('[data-draw-shape]') ||
        target.closest('.analysis-area-volume-selector') ||
        target.closest('.onboarding-nav') ||
        target.closest('button') ||
        target.closest('input') ||
        target.closest('a') ||
        target.closest('[role="button"]')
      ) {
        return;
      }
      onOutsideClick();
    };

    document.addEventListener('click', handleDocumentClick);
    return () => document.removeEventListener('click', handleDocumentClick);
  }, [enabled, onOutsideClick]);
};

export const getPolygonRefByType = (
  polygonType: BorderType | null,
  legalBorderRef: PolygonRef,
  buildableBorderRef: PolygonRef,
  analysisAreaRef: PolygonRef
): PolygonRef | null => {
  if (polygonType === 'legalBorder') {
    return legalBorderRef;
  }
  if (polygonType === 'buildableBorder') {
    return buildableBorderRef;
  }
  if (polygonType === 'analysisArea') {
    return analysisAreaRef;
  }
  return null;
};
